import { useEffect, useRef, useState } from 'react';
import type { Asset } from '@vidmakr/bindings/types';
import { DEFAULT_POOL, type AnimLoop, cleanFileName, estimateEncodeBytes, fileStamp, renderMinutesFor, validateEncode } from '@vidmakr/shared';
import { Button, Modal, Spinner } from '@/components/ui';
import { DEFAULT_GIF_US, GifNameField, GifOutputFields, LONG_GIF_US, MIN_GIF_US, PictureFrame, RangeEditor, US, gifEncode, useGifShape, type NameMode, type RangeMedia } from '@/components/GifMaker';
import { formatMinutes, useOrgPlan } from '@/lib/billing';
import { formatBytes } from '@/lib/format';
import { useMediaUrls } from '@/lib/media';
import { reducers } from '@/lib/stdb';
import { attempt } from '@/lib/toast';

/**
 * Clips a GIF out of a video in the library: mark the part on the filmstrip
 * (or play it and press I and O), pick a size and frame rate, and a worker
 * makes the GIF next to the video (a conversion of just that range), named
 * with a date stamp or a name of your own.
 */
export function GifDialog({ asset, onClose }: { asset: Asset | null; onClose: () => void }) {
  if (!asset) return null;
  return <GifDialogBody key={asset.id} asset={asset} onClose={onClose} />;
}

function GifDialogBody({ asset, onClose }: { asset: Asset; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const urls = useMediaUrls(asset.id, asset.proxyKey + asset.filmstripKey);
  const src = urls.proxy ?? urls.original;
  const dur = asset.durationUs;
  const fps = asset.fps || 30;
  const [range, setRange] = useState(() => ({ startUs: 0, endUs: Math.min(dur, DEFAULT_GIF_US) }));
  const [now, setNow] = useState(0);
  const [playing, setPlaying] = useState(false);
  const shape = useGifShape(asset.width, asset.height);
  const [outFps, setOutFps] = useState(15);
  const [loop, setLoop] = useState<AnimLoop>('forever');
  const [nameMode, setNameMode] = useState<NameMode>('stamp');
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [openedAt] = useState(() => Date.now());
  const billing = useOrgPlan(asset.orgId);
  const { startUs, endUs } = range;

  // While playing, the range loops (or stops at its end for a GIF that plays once): what plays is what the GIF shows.
  const rangeRef = useRef({ ...range, once: loop === 'once' });
  rangeRef.current = { ...range, once: loop === 'once' };
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    let raf = 0;
    const tick = () => {
      const t = v.currentTime * US;
      const r = rangeRef.current;
      if (!v.paused && t >= r.endUs) {
        if (r.once) v.pause();
        else v.currentTime = r.startUs / US;
      }
      setNow(v.currentTime * US);
      raf = requestAnimationFrame(tick);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, [src]);

  const media: RangeMedia = {
    durationUs: dur,
    timeUs: now,
    playing,
    fps,
    seek: (us) => {
      if (video.current) video.current.currentTime = us / US;
      setNow(us);
    },
    playRange: () => {
      const v = video.current;
      if (!v) return;
      if (v.currentTime * US < startUs || v.currentTime * US >= endUs - 50_000) v.currentTime = startUs / US;
      void v.play();
    },
    pause: () => video.current?.pause(),
    step: (frames) => {
      const v = video.current;
      if (!v) return;
      v.pause();
      v.currentTime = Math.max(0, Math.min(dur / US, v.currentTime + frames / fps));
    },
  };

  const base = asset.name.replace(/\.[A-Za-z0-9]{1,8}$/, '');
  const stampName = `${cleanFileName(base, 'gif') || 'clip'}_${fileStamp(openedAt)}`;
  const name = nameMode === 'stamp' ? stampName : cleanFileName(custom, 'gif');
  const { width, height } = shape;
  const encode = gifEncode(loop, shape);
  const lengthUs = endUs - startUs;
  // Back and forth plays the part twice.
  const outUs = lengthUs * (loop === 'bounce' ? 2 : 1);
  const problem =
    lengthUs < MIN_GIF_US ? 'The end must come after the start' : !name ? 'Name the GIF' : validateEncode(encode, { width, height, fps: outFps, gpu: false });
  const estimate = estimateEncodeBytes(encode, { width, height, fps: outFps, durationUs: outUs });
  const minutes = renderMinutesFor({ width, height, encode }, outUs);
  const outOfMinutes = !!billing?.enabled && minutes > billing.renderLeft + 1e-9;

  const start = async () => {
    setBusy(true);
    video.current?.pause();
    const ok = await attempt(
      () =>
        reducers().requestConvert({
          assetIds: [asset.id],
          encode: JSON.stringify(encode),
          width,
          height,
          fps: outFps,
          pool: DEFAULT_POOL,
          label: 'GIF',
          startUs: Math.round(startUs),
          endUs: Math.round(endUs),
          name,
        }),
      `Making “${name}.gif” · follow it under Exports`,
    );
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Modal open onClose={onClose} title={`Make a GIF from “${asset.name}”`} width={760}>
      <div className="space-y-4" data-gif-dialog>
        <PictureFrame shape={shape} onClick={() => (playing ? media.pause() : media.playRange())}>
          {src ? (
            <video ref={video} src={src} muted playsInline className="absolute inset-0 h-full w-full cursor-pointer object-fill" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-muted">
              <Spinner />
            </div>
          )}
        </PictureFrame>
        <RangeEditor
          media={media}
          startUs={startUs}
          endUs={endUs}
          onChange={(s, e) => setRange({ startUs: s, endUs: e })}
          filmstrip={urls.filmstrip && asset.filmstripTiles ? { url: urls.filmstrip, tiles: asset.filmstripTiles, intervalUs: asset.filmstripIntervalUs } : null}
          keys
        />
        <div className="grid grid-cols-2 gap-4">
          <GifOutputFields shape={shape} fps={outFps} onFps={setOutFps} loop={loop} onLoop={setLoop} />
          <div className="col-span-2">
            <GifNameField mode={nameMode} onMode={setNameMode} custom={custom} onCustom={setCustom} stampName={stampName} />
          </div>
        </div>
        {problem ? (
          <p className="text-xs text-danger" data-encode-error>
            {problem}
          </p>
        ) : lengthUs > LONG_GIF_US ? (
          <p className="text-xs text-warn">GIFs longer than {LONG_GIF_US / US} seconds get large; a shorter part or a smaller size keeps them light.</p>
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-muted">
            {width}×{height} · {outFps} fps{loop === 'bounce' ? ` · ${(outUs / US).toFixed(1)} s back and forth` : ''} · ≈ {formatBytes(estimate)}
          </span>
          <div className="flex-1" />
          {billing?.enabled ? (
            <span className={outOfMinutes ? 'text-xs text-danger' : 'text-xs text-muted'}>
              {formatMinutes(minutes)} render min · {formatMinutes(billing.renderLeft)} left
            </span>
          ) : null}
          <Button variant="primary" loading={busy} disabled={!!problem || outOfMinutes} onClick={start} data-start-gif>
            Make GIF
          </Button>
        </div>
      </div>
    </Modal>
  );
}
